import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Ellipsis } from "@air/next-icons";
import { DropdownMenu, renderDropdownItems } from "@air/primitive-dropdown-menu";
import { IconButton } from "@air/primitive-icon-button";
import { tailwindVariants } from "@air/tailwind-variants";
import { useRouter } from "next/router";
import { Fragment, useMemo } from "react";
import { BreadcrumbsButton } from "./BreadcrumbsButton";
import { BreadcrumbsItem } from "./BreadcrumbsItem";
import { BreadcrumbsLink } from "./BreadcrumbsLink";
import { BreadcrumbsSeparator } from "./BreadcrumbsSeparator";
export const breadcrumbs = tailwindVariants({
    base: "flex items-center gap-1 text-grey-9",
    variants: {
        size: {
            small: "text-12",
            medium: "text-14",
            large: "text-16"
        }
    },
    defaultVariants: {
        size: "medium"
    }
});
export const Breadcrumbs = (param)=>{
    let { className, isCompact: _isCompact = false, items = [], separator = "/", size = "medium", visibleItemsAtEnd = 1, ...restOfProps } = param;
    const { push } = useRouter();
    const isCompact = _isCompact && items.length > 2;
    const itemRenderer = (item)=>{
        const isLastItem = items.indexOf(item) === items.length - 1;
        const element = item.type === "button" ? /*#__PURE__*/ _jsx(BreadcrumbsButton, {
            className: item.className,
            onClick: item.onClick,
            prefix: item.prefix,
            suffix: item.suffix,
            "data-item-id": item.id,
            children: item.label
        }, "breadcrumbs-button-".concat(item.id)) : item.type === "item" ? /*#__PURE__*/ _jsx(BreadcrumbsItem, {
            className: item.className,
            prefix: item.prefix,
            suffix: item.suffix,
            "data-item-id": item.id,
            children: item.label
        }, "breadcrumbs-item-".concat(item.id)) : item.type === "link" ? /*#__PURE__*/ _jsx(BreadcrumbsLink, {
            className: item.className,
            href: item.href,
            prefix: item.prefix,
            suffix: item.suffix,
            "data-item-id": item.id,
            children: item.label
        }, "breadcrumbs-link-".concat(item.id)) : null;
        return isLastItem ? element : /*#__PURE__*/ _jsxs(Fragment, {
            children: [
                element,
                /*#__PURE__*/ _jsx(BreadcrumbsSeparator, {
                    children: separator
                }, "breadcrumbs-separator-".concat(item.id))
            ]
        }, "breadcrumbs-fragment-".concat(item.id));
    };
    const filteredItems = useMemo(()=>{
        if (isCompact) {
            return items.slice(-visibleItemsAtEnd);
        }
        return items;
    }, [
        isCompact,
        items,
        visibleItemsAtEnd
    ]);
    const menuOptions = useMemo(()=>{
        return items.slice(1, items.length - visibleItemsAtEnd).map((item)=>({
                id: item.id,
                label: item.label,
                onSelect: ()=>{
                    if (item.type === "link") {
                        push(item.href);
                    }
                    if (item.type === "button") {
                        item.onClick();
                    }
                },
                type: "item"
            }));
    }, [
        items,
        push,
        visibleItemsAtEnd
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: breadcrumbs({
            size,
            class: className
        }),
        ...restOfProps,
        children: [
            isCompact && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    itemRenderer(items[0]),
                    /*#__PURE__*/ _jsx(DropdownMenu, {
                        "data-testid": "MORE_ANCESTORS_DROPDOWN",
                        align: "start",
                        sideOffset: 8,
                        trigger: /*#__PURE__*/ _jsx(IconButton, {
                            "data-testid": "MORE_ANCESTORS_BUTTON",
                            appearance: "ghost",
                            className: "text-grey-8",
                            color: "grey",
                            icon: Ellipsis,
                            label: "More ancestors",
                            size: "small"
                        }),
                        children: renderDropdownItems({
                            options: menuOptions
                        })
                    }),
                    /*#__PURE__*/ _jsx(BreadcrumbsSeparator, {
                        children: separator
                    })
                ]
            }),
            filteredItems.map(itemRenderer)
        ]
    });
};
